import { 
  FieldSpec, RecordSpec, FileSpec, 
  ValidationError, ParsedRecord, ValidationResults, 
  AIInsight, CommonIssue, SpecType 
} from '../types';
import { validateFieldType, validateFieldLength, isValidDate, isValidTime, isValidNumeric } from '../utils/validators';

// Raw JSON spec field definition shape
interface RawFieldDef {
  name: string;
  num: number;
  min?: number;
  max: number;
  format?: string;
  type: string;
  usage: string;
  values?: string | string[];
}

interface RawRecordDef {
  name?: string;
  required?: boolean;
  maxOccurrences?: number;
  fields: RawFieldDef[];
}

interface RawSpecDef {
  file_type?: string;
  description?: string;
  version?: string;
  delimiter?: string;
  record_length?: number;
  newline?: string;
  file_naming?: string;
  records: Record<string, RawRecordDef>;
}

type RawSpecKey = 'CLAIM_STATUS_30' | 'ELIGIBILITY_31' | 'ELIGIBILITY_30' | 'INDIVIDUAL_USAGE_BENEFIT_30' | 'PLAN_BENEFIT_30' | 'STANDARD_BENEFIT_ENTITY_30';

// Load JSON-based specifications (delimited specs) from inside src
const specDefinitions: Record<RawSpecKey, RawSpecDef> = require('../specifications/hps_specifications.json');

// Runtime validation configuration (tunable)
const validationConfig = {
  strictOrdering: true, // if true, DTL/STC before CLM are errors; otherwise warnings
  strictTrailerCount: true, // if true, trailer count mismatches are errors; otherwise warnings
  allowLFNewline: true // if true, accept LF-only files and do not warn
};

function mapJsonTypeToDataType(type: string): FieldSpec['dataType'] {
  if (!type) return 'AN';
  const t = type.toUpperCase();
  if (t === 'N') return 'N';
  if (t === 'AN') return 'AN';
  if (t === 'DT' || t === 'D8') return 'DT';
  if (t === 'TM' || t === 'T6') return 'TM';
  return 'AN';
}

function convertJsonToFileSpec(key: RawSpecKey): FileSpec {
  const raw = specDefinitions[key];
  const delimiter = raw?.delimiter || '|';
  const records: Record<string, RecordSpec> = {};

  for (const [recKey, recDef] of Object.entries(raw.records || {})) {
    const fields: FieldSpec[] = (recDef.fields || []).map((f) => {
      let usage: FieldSpec['usage'];
      if (f.usage === 'R') {
        usage = 'Required';
      } else if (f.usage === 'C') {
        usage = 'Conditional';
      } else {
        usage = 'Optional';
      }
      let values: string[] | undefined;
      if (Array.isArray(f.values)) {
        values = f.values;
      } else if (f.values) {
        values = [f.values];
      }
      return {
        name: f.name,
        num: f.num,
        min: f.min,
        max: f.max,
        format: f.format,
        dataType: mapJsonTypeToDataType(f.type),
        required: f.usage === 'R',
        usage,
        values
      };
    });

    records[String(recKey)] = {
      name: recDef.name || String(recKey),
      code: String(recKey),
      required: !!recDef.required,
      maxOccurrences: recDef.maxOccurrences || 999999,
      fields
    };
  }

  return {
    name: raw.file_type || raw.description || key,
    version: raw.version || '1.0',
    description: raw.description || key,
    delimiter,
    recordLength: raw.record_length || 0,
    newline: raw.newline,
    fileNaming: raw.file_naming,
    records
  };
}

const _PDF_BASE = process.env.PUBLIC_URL || '';

const SPEC_PDF_URLS: Record<string, string> = {
  claimstatus30:            `${_PDF_BASE}/specs/Optum Hosted File Spec - Claim Status 3.0.pdf`,
  eligibility31:            `${_PDF_BASE}/specs/Optum Hosted File Spec - Eligibility 3.1.pdf`,
  eligibility30:            `${_PDF_BASE}/specs/Optum Hosted File Spec - Eligibility 3.0 5.pdf`,
  individualusagebenefit30: `${_PDF_BASE}/specs/Optum Hosted File Spec - Individual Usage Benefit 3.0.pdf`,
  planbenefit30:            `${_PDF_BASE}/specs/Optum Hosted File Spec - Plan Benefit 3.0 4.pdf`,
  standardbenefitentity30:  `${_PDF_BASE}/specs/Optum Hosted File SPec - Standard Benefit Entity 3.0 4.pdf`,
};

// Provide FileSpec derived from JSON definitions for delimited files
export function getHpsSpec(specType: SpecType): FileSpec {
  let spec: FileSpec;
  if (specType === 'claimstatus30') spec = convertJsonToFileSpec('CLAIM_STATUS_30');
  else if (specType === 'eligibility30') spec = convertJsonToFileSpec('ELIGIBILITY_30');
  else if (specType === 'individualusagebenefit30') spec = convertJsonToFileSpec('INDIVIDUAL_USAGE_BENEFIT_30');
  else if (specType === 'planbenefit30') spec = convertJsonToFileSpec('PLAN_BENEFIT_30');
  else if (specType === 'standardbenefitentity30') spec = convertJsonToFileSpec('STANDARD_BENEFIT_ENTITY_30');
  else spec = convertJsonToFileSpec('ELIGIBILITY_31');
  spec.specPdfUrl = SPEC_PDF_URLS[specType];
  return spec;
}

export function getRecordType(line: string, spec?: FileSpec): string {
  if (spec && spec.delimiter) {
    const tokens = line.split(spec.delimiter);
    // Prefer the first non-empty token in the first few positions
    let first = (tokens[0] || '').trim();
    if (!first) {
      for (let i = 1; i < Math.min(tokens.length, 4); i++) {
        const t = (tokens[i] || '').trim();
        if (t) {
          first = t;
          break;
        }
      }
    }
    first = (first || '').toUpperCase();
    if (first === 'TRLR') return 'TRLR';
    return first;
  }
  const trimmed = line.substring(0, 4).trim();
  if (trimmed === 'TRLR') return 'TRLR';
  return line.substring(0, 3).trim();
}

export function parseRecord(line: string, recordSpec: RecordSpec, lineNumber: number, spec?: FileSpec): ParsedRecord {
  const fields: Record<string, string> = {};
  if (spec && spec.delimiter) {
    const tokens = line.split(spec.delimiter);
    for (const field of recordSpec.fields) {
      const idx = (field.num || 0) - 1;
      const value = (tokens[idx] || '').trim();
      fields[field.name] = value;
    }
  } else {
    for (const field of recordSpec.fields) {
      const start = (field.start || 1) - 1;
      const end = field.end || (start + (field.length || 1));
      const value = line.substring(start, end).trim();
      fields[field.name] = value;
    }
  }

  return {
    line: lineNumber,
    recordType: recordSpec.code,
    rawData: line,
    fields
  };
}

function getRawFieldValue(record: ParsedRecord, field: FieldSpec, spec?: FileSpec): string {
  if (spec && spec.delimiter) {
    const tokens = record.rawData.split(spec.delimiter);
    return (tokens[(field.num || 0) - 1] || '');
  }
  const start = (field.start || 1) - 1;
  const end = field.end || (start + (field.length || 1));
  return record.rawData.substring(start, end);
}

function validateFieldRequiredAndLength(
  record: ParsedRecord,
  field: FieldSpec,
  value: string,
  rawValue: string,
  spec?: FileSpec
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (field.required && !value.trim()) {
    errors.push({
      line: record.line,
      field: field.name,
      message: `Required field "${field.name}" is empty`,
      expected: `Non-empty ${field.dataType} value`,
      actual: '(empty)',
      severity: 'error',
      fixSuggestion: `Populate the ${field.name} field with a valid ${getDataTypeDescription(field.dataType)} value`
    });
    return errors;
  }

  if (!value.trim()) return errors;

  if (spec && spec.delimiter) {
    const min = field.min || 0;
    const max = field.max || Infinity;
    if (!validateFieldLength(value, min, max)) {
      errors.push({
        line: record.line,
        field: field.name,
        message: `Field "${field.name}" length out of range`,
        expected: `${min}-${max} characters`,
        actual: `${value.length} characters`,
        severity: 'error',
        fixSuggestion: `Adjust the ${field.name} length to be within ${min}-${max} characters`
      });
    }
  } else if (field.length !== undefined && rawValue.length !== field.length) {
    errors.push({
      line: record.line,
      field: field.name,
      message: `Field "${field.name}" has incorrect length`,
      expected: `${field.length} characters`,
      actual: `${rawValue.length} characters`,
      severity: 'error',
      fixSuggestion: `Adjust the field to exactly ${field.length} characters (pad with spaces if needed)`
    });
  }

  return errors;
}

function validateFieldTypeAndValues(record: ParsedRecord, field: FieldSpec, value: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!validateFieldType(value, field.dataType, field.format)) {
    errors.push({
      line: record.line,
      field: field.name,
      message: `Field "${field.name}" has invalid data type`,
      expected: getDataTypeDescription(field.dataType),
      actual: value,
      severity: 'error',
      fixSuggestion: getDataTypeFix(field.dataType, field.name)
    });
  }

  if (field.values && field.values.length > 0) {
    const allowed = field.values.map(v => String(v).trim().toUpperCase());
    if (!allowed.includes(value.trim().toUpperCase())) {
      errors.push({
        line: record.line,
        field: field.name,
        message: `Field "${field.name}" has invalid value`,
        expected: `One of: ${field.values.join(', ')}`,
        actual: value,
        severity: 'error',
        fixSuggestion: `Change value to one of the allowed values: ${field.values.join(', ')}`
      });
    }
  }

  return errors;
}

export function validateRecord(record: ParsedRecord, recordSpec: RecordSpec, spec?: FileSpec): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const field of recordSpec.fields) {
    const value = record.fields[field.name] || '';
    const rawValue = getRawFieldValue(record, field, spec);

    errors.push(...validateFieldRequiredAndLength(record, field, value, rawValue, spec));
    if (!value.trim()) continue;

    errors.push(...validateFieldTypeAndValues(record, field, value));
  }

  return errors;
}

// Heuristic conditional rules applied across records
function applyConditionalHeuristics(record: ParsedRecord, recordSpec: RecordSpec): ValidationError[] {
  const errs: ValidationError[] = [];
  const fields = recordSpec.fields;
  const values = record.fields;

  const maintenanceTypeKey = fields.find(f => /maintenance type code/i.test(f.name))?.name;
  const maintenanceTypeVal = maintenanceTypeKey ? (values[maintenanceTypeKey] || '').trim() : '';

  // Rule 1 (CLM): Only require conditional billing provider fields for certain maintenance types
  if (recordSpec.code === 'CLM' && maintenanceTypeVal) {
    // Common maintenance types that imply provider info is required (heuristic)
    const requireProviderFor = ['001', '002'];
    if (requireProviderFor.includes(maintenanceTypeVal)) {
      for (const f of fields.filter(x => x.usage === 'Conditional')) {
        const v = (values[f.name] || '').trim();
        if (!v) {
          errs.push({
            line: record.line,
            field: f.name,
            message: `Field "${f.name}" is required for Maintenance Type ${maintenanceTypeVal}`,
            expected: `Non-empty when Maintenance Type Code = ${maintenanceTypeVal}`,
            actual: '(empty)',
            severity: 'error',
            fixSuggestion: `Populate ${f.name} when Maintenance Type Code is ${maintenanceTypeVal}`
          });
        }
      }
    }
  }

  // Rule 2: If Action Indicator == 'D' (delete), require Maintenance Reason Code if present in spec
  const actionKey = fields.find(f => /action indicator/i.test(f.name))?.name;
  const actionVal = actionKey ? (values[actionKey] || '').trim().toUpperCase() : '';
  if (actionVal === 'D') {
    const reasonKey = fields.find(f => /maintenance reason code/i.test(f.name))?.name;
    if (reasonKey) {
      const rv = (values[reasonKey] || '').trim();
      if (!rv) {
        errs.push({
          line: record.line,
          field: reasonKey,
          message: `Field "${reasonKey}" is required when Action Indicator is 'D'`,
          expected: `Non-empty value when Action Indicator = D`,
          actual: '(empty)',
          severity: 'error',
          fixSuggestion: `Provide a Maintenance Reason Code when deleting a record`
        });
      }
    }
  }

  // Rule 3: If Correction Indicator == 'Y', require Maintenance Reason Code (common heuristic)
  const correctionKey = fields.find(f => /correction indicator/i.test(f.name))?.name;
  const correctionVal = correctionKey ? (values[correctionKey] || '').trim().toUpperCase() : '';
  if (correctionVal === 'Y') {
    const reasonKey = fields.find(f => /maintenance reason code/i.test(f.name))?.name;
    if (reasonKey) {
      const rv = (values[reasonKey] || '').trim();
      if (!rv) {
        errs.push({
          line: record.line,
          field: reasonKey,
          message: `Field "${reasonKey}" is required when Correction Indicator = 'Y'`,
          expected: `Non-empty value when Correction Indicator = Y`,
          actual: '(empty)',
          severity: 'error',
          fixSuggestion: `Provide a Maintenance Reason Code when Correction Indicator = Y`
        });
      }
    }
  }

  return errs;
}

function validateStructure(
  records: ParsedRecord[],
  hasHeader: boolean,
  hasTrailer: boolean,
  lines: string[],
  errors: ValidationError[]
): void {
  if (!hasHeader) {
    errors.unshift({
      line: 1,
      field: 'File Structure',
      message: 'Missing required HDR (Header) record',
      expected: 'HDR record at start of file',
      actual: 'No HDR record found',
      severity: 'error',
      fixSuggestion: 'Add a valid Header (HDR) record at the beginning of the file'
    });
  }

  if (!hasTrailer) {
    errors.push({
      line: lines.length,
      field: 'File Structure',
      message: 'Missing required TRLR (Trailer) record',
      expected: 'TRLR record at end of file',
      actual: 'No TRLR record found',
      severity: 'error',
      fixSuggestion: 'Add a valid Trailer (TRLR) record at the end of the file'
    });
  }

  const firstNonEmpty = records.find(r => (r.rawData || '').trim() !== '');
  if (firstNonEmpty && firstNonEmpty.recordType !== 'HDR') {
    errors.unshift({
      line: firstNonEmpty.line,
      field: 'File Structure',
      message: 'HDR record not at start of file',
      expected: 'First record should be HDR',
      actual: firstNonEmpty.recordType,
      severity: 'error',
      fixSuggestion: 'Place HDR as the first record'
    });
  }

  const lastNonEmpty = [...records].reverse().find(r => (r.rawData || '').trim() !== '');
  if (lastNonEmpty && lastNonEmpty.recordType !== 'TRLR') {
    errors.push({
      line: lastNonEmpty.line,
      field: 'File Structure',
      message: 'TRLR record not at end of file',
      expected: 'Last record should be TRLR',
      actual: lastNonEmpty.recordType,
      severity: 'error',
      fixSuggestion: 'Place TRLR as the last record'
    });
  }
}

function validateRecordOccurrences(
  spec: FileSpec,
  recordCounts: Record<string, number>,
  errors: ValidationError[]
): void {
  for (const [code, rSpec] of Object.entries(spec.records)) {
    const count = recordCounts[code] || 0;
    if (rSpec.required && count === 0) {
      errors.push({
        line: 1,
        field: 'Record Occurrence',
        message: `Required record ${code} missing`,
        expected: `At least one ${code} record`,
        actual: `${count}`,
        severity: 'error',
        fixSuggestion: `Add a ${code} record as specified`
      });
    }
    if (rSpec.maxOccurrences && count > rSpec.maxOccurrences) {
      errors.push({
        line: 1,
        field: 'Record Occurrence',
        message: `Record ${code} appears ${count} times (max ${rSpec.maxOccurrences})`,
        expected: `No more than ${rSpec.maxOccurrences} ${code} records`,
        actual: `${count}`,
        severity: 'error',
        fixSuggestion: `Reduce ${code} occurrences to ${rSpec.maxOccurrences} or fewer`
      });
    }
  }
}

function validateTrailerCount(
  records: ParsedRecord[],
  recordCounts: Record<string, number>,
  errors: ValidationError[],
  warnings: ValidationError[]
): void {
  const trlrRecords = records.filter(r => r.recordType === 'TRLR');
  if (trlrRecords.length === 0) return;

  const trlr = trlrRecords[trlrRecords.length - 1];
  const countFieldName = Object.keys(trlr.fields).find(n => /record count/i.test(n));
  if (!countFieldName) return;

  const declared = parseInt(trlr.fields[countFieldName], 10);
  const expected = records.length - (recordCounts['HDR'] || 0) - trlrRecords.length;
  if (!isNaN(declared) && declared !== expected) {
    const sev: 'error' | 'warning' = validationConfig.strictTrailerCount ? 'error' : 'warning';
    (sev === 'error' ? errors : warnings).push({
      line: trlr.line,
      field: 'TRLR.Record Count',
      message: `Trailer record count ${declared} does not match expected ${expected}`,
      expected: `${expected}`,
      actual: `${declared}`,
      severity: sev,
      fixSuggestion: `Update TRLR record count to ${expected}`
    });
  }
}

function validateRecordOrdering(
  records: ParsedRecord[],
  errors: ValidationError[],
  warnings: ValidationError[]
): void {
  let lastClmSeen: number | null = null;
  for (const r of records) {
    if (r.recordType === 'CLM') lastClmSeen = r.line;
    if ((r.recordType === 'DTL' || r.recordType === 'STC') && lastClmSeen === null) {
      const sev: 'error' | 'warning' = validationConfig.strictOrdering ? 'error' : 'warning';
      (sev === 'error' ? errors : warnings).push({
        line: r.line,
        field: 'Record Order',
        message: `${r.recordType} found before CLM parent record`,
        expected: 'Parent CLM should appear before child records',
        actual: r.recordType,
        severity: sev,
        fixSuggestion: `Reorder file so ${r.recordType} occurs after a CLM record`
      });
    }
  }
}

function validateDateRanges(
  records: ParsedRecord[],
  errors: ValidationError[]
): void {
  for (const rec of records) {
    const keys = Object.keys(rec.fields);
    for (const k of keys) {
      if (!/start/i.test(k)) continue;
      const endKey = keys.find(
        x => new RegExp(k.replace(/start/i, ''), 'i').test(x) && /end/i.test(x)
      );
      if (!endKey) continue;
      const s = rec.fields[k];
      const e = rec.fields[endKey];
      if (s && e && s.length >= 8 && e.length >= 8) {
        const sd = parseInt(s.substring(0, 8), 10);
        const ed = parseInt(e.substring(0, 8), 10);
        if (!isNaN(sd) && !isNaN(ed) && sd > ed) {
          errors.push({
            line: rec.line,
            field: `${k}/${endKey}`,
            message: `Start date ${s} is after End date ${e}`,
            expected: `Start <= End`,
            actual: `${s} > ${e}`,
            severity: 'error',
            fixSuggestion: 'Ensure date ranges are logical (Start before End)'
          });
        }
      }
    }
  }
}

function validateNewlines(
  content: string,
  spec: FileSpec,
  warnings: ValidationError[]
): void {
  if (!spec.newline) return;
  const expectedNewline = String(spec.newline).toUpperCase();
  const hasCRLF = content.includes('\r\n');
  if (expectedNewline.includes('CRLF') && !hasCRLF) {
    warnings.push({
      line: 1,
      field: 'File Format',
      message: `Spec expects CRLF newlines but file appears to use LF`,
      expected: 'CRLF line endings',
      actual: 'LF',
      severity: 'warning',
      fixSuggestion: 'Convert file to CRLF newlines'
    });
  }
}

function validateFilename(
  filename: string | undefined,
  spec: FileSpec,
  warnings: ValidationError[]
): void {
  if (!filename || !spec.fileNaming) return;
  const pattern = String(spec.fileNaming).trim();

  const expectedExtMatch = pattern.match(/\.([a-zA-Z0-9]+)$/);
  if (expectedExtMatch) {
    const ext = expectedExtMatch[1];
    if (!filename.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
      warnings.push({
        line: 1,
        field: 'File Name',
        message: `Filename does not match expected pattern: ${pattern}`,
        expected: pattern,
        actual: filename,
        severity: 'warning',
        fixSuggestion: `Rename file to match pattern ${pattern}`
      });
    }
  }

  if (/CCYYMMDD|CCYYMMDDHHMMSS|CCYYMMDDHHMM/.test(pattern)) {
    const digits = filename.match(/(\d{8,14})/);
    if (!digits) {
      warnings.push({
        line: 1,
        field: 'File Name',
        message: `Filename does not contain expected timestamp per pattern ${pattern}`,
        expected: 'Timestamp in filename',
        actual: filename,
        severity: 'warning',
        fixSuggestion: `Include timestamp (e.g., CCYYMMDDHHMMSS) in filename as ${pattern}`
      });
    }
  }
}

interface FileLineContext {
  errors: ValidationError[];
  warnings: ValidationError[];
  records: ParsedRecord[];
  recordCounts: Record<string, number>;
  hasHeader: boolean;
  hasTrailer: boolean;
}

function validateLineLength(normalizedLine: string, lineNumber: number, spec: FileSpec, ctx: FileLineContext): void {
  if ((!spec.delimiter || spec.delimiter === '') && spec.recordLength && spec.recordLength > 0) {
    if (normalizedLine.length !== spec.recordLength) {
      ctx.warnings.push({
        line: lineNumber,
        field: 'Record',
        message: `Record length mismatch`,
        expected: `${spec.recordLength} characters`,
        actual: `${normalizedLine.length} characters`,
        severity: 'warning',
        fixSuggestion: `Adjust record to exactly ${spec.recordLength} characters`
      });
    }
  }
}

function processFileLine(normalizedLine: string, lineNumber: number, spec: FileSpec, ctx: FileLineContext): void {
  validateLineLength(normalizedLine, lineNumber, spec, ctx);

  const recordType = getRecordType(normalizedLine, spec);
  const recordSpec = spec.records[recordType];

  if (!recordSpec) {
    ctx.errors.push({
      line: lineNumber,
      field: 'Record Type',
      message: `Unknown record type: "${recordType}"`,
      expected: `One of: ${Object.keys(spec.records).join(', ')}`,
      actual: recordType,
      severity: 'error',
      fixSuggestion: `Use a valid record type: ${Object.keys(spec.records).join(', ')}`
    });
    return;
  }

  ctx.recordCounts[recordType] = (ctx.recordCounts[recordType] || 0) + 1;
  if (recordType === 'HDR') ctx.hasHeader = true;
  if (recordType === 'TRLR') ctx.hasTrailer = true;

  const record = parseRecord(normalizedLine, recordSpec, lineNumber, spec);
  ctx.records.push(record);

  const recordErrors = validateRecord(record, recordSpec, spec);
  ctx.errors.push(...recordErrors.filter(e => e.severity === 'error'));
  ctx.warnings.push(...recordErrors.filter(e => e.severity === 'warning'));
  ctx.errors.push(...applyConditionalHeuristics(record, recordSpec));
}

export function validateFile(content: string, spec: FileSpec, filename?: string): ValidationResults {
  const lines = content.split(/\r?\n/);
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  const records: ParsedRecord[] = [];
  const recordCounts: Record<string, number> = {};
  const ctx: FileLineContext = { errors, warnings, records, recordCounts, hasHeader: false, hasTrailer: false };

  for (let i = 0; i < lines.length; i++) {
    const normalizedLine = lines[i].replace(/\r$/, '');
    if (!normalizedLine.trim()) continue;
    processFileLine(normalizedLine, i + 1, spec, ctx);
  }

  validateStructure(records, ctx.hasHeader, ctx.hasTrailer, lines, errors);

  validateRecordOccurrences(spec, recordCounts, errors);
  validateTrailerCount(records, recordCounts, errors, warnings);
  validateRecordOrdering(records, errors, warnings);
  validateDateRanges(records, errors);
  validateNewlines(content, spec, warnings);
  validateFilename(filename, spec, warnings);

  const errorLines = new Set(errors.map(e => e.line));
  const validRecords = records.filter(r => !errorLines.has(r.line)).length;

  return {
    isValid: errors.length === 0,
    totalRecords: records.length,
    validRecords,
    errors,
    warnings,
    records,
    recordCounts
  };
}

export function calculateHealthScore(results: ValidationResults): number {
  if (results.totalRecords === 0) return 0;
  
  const errorWeight = 3;
  const warningWeight = 1;
  
  const totalIssues = (results.errors.length * errorWeight) + (results.warnings.length * warningWeight);
  const maxIssues = results.totalRecords * 5;
  
  const score = Math.max(0, 100 - (totalIssues / maxIssues * 100));
  return Math.round(score);
}

export function generateAIInsights(results: ValidationResults, spec: FileSpec): AIInsight[] {
  const insights: AIInsight[] = [];
  
  // Overall status insight
  if (results.isValid) {
    insights.push({
      type: 'success',
      title: 'File Validation Passed',
      description: 'All records comply with the specification. File is ready for processing.',
      action: 'No action required'
    });
  } else {
    insights.push({
      type: 'error',
      title: 'Validation Issues Found',
      description: `Found ${results.errors.length} errors and ${results.warnings.length} warnings that need attention.`,
      action: 'Review the error list below and fix each issue'
    });
  }
  
  // Data quality insight
  const validRatio = results.validRecords / results.totalRecords;
  if (validRatio >= 0.95) {
    insights.push({
      type: 'success',
      title: 'High Data Quality',
      description: `${Math.round(validRatio * 100)}% of records passed validation.`,
      action: 'Minor cleanup needed for remaining records'
    });
  } else if (validRatio >= 0.7) {
    insights.push({
      type: 'warning',
      title: 'Moderate Data Quality',
      description: `${Math.round(validRatio * 100)}% of records passed validation.`,
      action: 'Review common error patterns to batch fix issues'
    });
  } else {
    insights.push({
      type: 'error',
      title: 'Low Data Quality',
      description: `Only ${Math.round(validRatio * 100)}% of records passed validation.`,
      action: 'Consider regenerating the file from source system'
    });
  }
  
  // Pattern detection
  const errorsByField = groupBy(results.errors, 'field');
  const topErrorField = Object.entries(errorsByField).sort((a, b) => b[1].length - a[1].length)[0];
  
  if (topErrorField && topErrorField[1].length > 2) {
    insights.push({
      type: 'info',
      title: 'Pattern Detected',
      description: `The "${topErrorField[0]}" field has ${topErrorField[1].length} errors - this might indicate a systematic issue.`,
      action: 'Check the source system mapping for this field'
    });
  }
  
  return insights;
}

export function getCommonIssues(results: ValidationResults): CommonIssue[] {
  const issues: CommonIssue[] = [];
  const errorsByMessage = groupBy(results.errors, 'message');
  
  for (const [message, errors] of Object.entries(errorsByMessage)) {
    if (errors.length >= 2) {
      issues.push({
        issue: message,
        count: errors.length,
        affectedLines: errors.map(e => e.line),
        suggestion: errors[0].fixSuggestion || 'Review the specification for this field'
      });
    }
  }
  
  return issues.sort((a, b) => b.count - a.count).slice(0, 5);
}

function getDataTypeDescription(dataType: string): string {
  const types: Record<string, string> = {
    'N': 'Numeric (0-9 only)',
    'AN': 'Alphanumeric',
    'DT': 'Date (YYYYMMDD)',
    'TM': 'Time (HHMMSS)'
  };
  return types[dataType] || dataType;
}

function getDataTypeFix(dataType: string, fieldName: string): string {
  const fixes: Record<string, string> = {
    'N': `Ensure ${fieldName} contains only digits (0-9)`,
    'DT': `Format ${fieldName} as YYYYMMDD (e.g., 20250115)`,
    'TM': `Format ${fieldName} as HHMMSS (e.g., 143000)`,
    'AN': `Remove any special characters from ${fieldName}`
  };
  return fixes[dataType] || `Format ${fieldName} correctly`;
}

function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const group = String(item[key]);
    (result[group] = result[group] || []).push(item);
    return result;
  }, {} as Record<string, T[]>);
}
